import React, { useCallback, useEffect, useRef, useState } from "react";
import Dialog from "@material-ui/core/Dialog";
import Slide from "@material-ui/core/Slide";
import { useTranslation } from "react-i18next";
import bookingNotesService, {
  formatCommentTimestamp,
} from "../../../services/bookingNotesService";
import BookingNotesList from "./BookingNotesList";
import BookingNoteComposer from "./BookingNoteComposer";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="left" ref={ref} {...props} />;
});

function BookingNotesDrawer({
  open,
  onClose,
  pnr,
  currentUserId,
  onNotesChange,
}) {
  const { t } = useTranslation();
  const [notes, setNotes] = useState([]);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [now, setNow] = useState(Date.now());

  const onNotesChangeRef = useRef(onNotesChange);
  onNotesChangeRef.current = onNotesChange;

  const formatTimestamp = formatCommentTimestamp;

  const loadNotes = useCallback(async () => {
    if (!pnr) {
      setNotes([]);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const result = await bookingNotesService.getNotes(pnr);
      setNotes(result);
      if (onNotesChangeRef.current) {
        onNotesChangeRef.current(result.length);
      }
    } catch (err) {
      setError(err?.message || t("Unable to load notes."));
    } finally {
      setLoading(false);
    }
  }, [pnr, t]);

  useEffect(() => {
    if (open) {
      loadNotes();
      setDraft("");
    }
  }, [open, loadNotes]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    setNow(Date.now());
    const intervalId = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(intervalId);
  }, [open]);

  const handleUpdated = (updated) => {
    setNotes((prev) =>
      prev.map((note) => (note.id === updated.id ? updated : note))
    );
  };

  const handleDeleted = (id) => {
    setNotes((prev) => {
      const next = prev.filter((note) => note.id !== id);
      if (onNotesChange) {
        onNotesChange(next.length);
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    const trimmed = draft.trim();
    if (!trimmed || sending || !currentUserId) {
      return;
    }
    setSending(true);
    setError("");
    try {
      const created = await bookingNotesService.createNote({
        pnr,
        text: trimmed,
        createdBy: currentUserId,
      });
      setNotes((prev) => {
        const next = [...prev, created];
        if (onNotesChange) {
          onNotesChange(next.length);
        }
        return next;
      });
      setDraft("");
      setNow(Date.now());
    } catch (err) {
      setError(err?.message || t("Unable to add note."));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      maxWidth={false}
      hideBackdrop={false}
      className="bd-notes-drawer-dialog"
      PaperProps={{
        className: "bd-notes-drawer-paper",
        square: true,
      }}
      BackdropProps={{
        className: "bd-notes-drawer-backdrop",
      }}
      aria-labelledby="bd-notes-drawer-title"
    >
      <div className="bd-notes-drawer">
        <header className="bd-notes-drawer-header">
          <div className="bd-notes-drawer-heading">
            <h2 id="bd-notes-drawer-title">{t("Notes")}</h2>
            <p className="bd-notes-drawer-pnr">
              {t("PNR: {{pnr}}", { pnr: pnr || "—" })}
            </p>
          </div>
          <button
            type="button"
            className="bd-notes-close-btn"
            onClick={onClose}
            aria-label={t("Close")}
          >
            <i className="fas fa-times" aria-hidden="true"></i>
          </button>
        </header>

        {loading ? (
          <div className="bd-notes-list">
            <p className="bd-notes-status">{t("Loading notes...")}</p>
          </div>
        ) : (
          <BookingNotesList
            notes={notes}
            currentUserId={currentUserId}
            now={now}
            formatTimestamp={formatTimestamp}
            onUpdated={handleUpdated}
            onDeleted={handleDeleted}
          />
        )}

        {error ? <p className="bd-notes-banner-error">{error}</p> : null}

        <BookingNoteComposer
          value={draft}
          onChange={setDraft}
          onSubmit={handleSubmit}
          disabled={!pnr || !currentUserId}
          sending={sending}
        />
      </div>
    </Dialog>
  );
}

export default BookingNotesDrawer;
